import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, BookOpen, Check, Ear, Pencil, PlayCircle, RotateCcw, Star, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playBigCheers } from "@/lib/sfx";
import { playSay, stopReading } from "@/lib/readingAudio";
import KikoCheer from "@/components/KikoCheer";
import ExerciseRunner from "@/components/reading/ExerciseRunner";
import FlashCards from "@/components/reading/FlashCards";
import { ReadingBg, Btn } from "@/pages/ReadingHome";
import Level3Lesson, { DEMO_MORE } from "@/pages/ReadingLevel3";
import { isReadingApp } from "@/lib/readingApp";

const STEPS = [
  { id: "watch", Icon: PlayCircle, en: "Watch", fr: "Regarde" },
  { id: "listen", Icon: Ear, en: "Listen", fr: "Écoute" },
  { id: "practise", Icon: Pencil, en: "Practise", fr: "Entraîne-toi" },
  { id: "read", Icon: BookOpen, en: "Read", fr: "Lis" },
  { id: "mastery", Icon: Trophy, en: "Check", fr: "Vérifie" },
];

function Steps({ step, fr }) {
  const idx = STEPS.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2" data-testid="lesson-steps">
      {STEPS.map((s, k) => (
        <div key={s.id} className="flex items-center gap-1 sm:gap-2">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-[3px] border-slate-900 flex items-center justify-center ${k < idx ? "bg-green-400" : k === idx ? "bg-yellow-300" : "bg-white"}`}
            title={fr ? s.fr : s.en} data-testid={`lesson-step-${s.id}`}>
            {k < idx ? <Check size={18} /> : <s.Icon size={18} />}
          </div>
          {k < STEPS.length - 1 && <div className={`w-3 sm:w-8 h-1 rounded ${k < idx ? "bg-green-500" : "bg-slate-300"}`} />}
        </div>
      ))}
    </div>
  );
}

function Watch({ videos, fr, onReady }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  return (
    <div className="w-full max-w-3xl flex flex-col items-center gap-4" data-testid="lesson-watch">
      <video key={videos[v]} ref={ref} src={videos[v]} controls controlsList="nodownload" disablePictureInPicture playsInline autoPlay className="w-full rounded-3xl border-[3px] border-slate-900 bg-black shadow-[8px_8px_0_0_#0F172A]" data-testid="lesson-video" />
      {videos.length > 1 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {videos.map((u, k) => (
            <button key={u} onClick={() => setV(k)} data-testid={`lesson-video-tab-${k}`}
              className={`rounded-full border-2 border-slate-900 px-4 h-10 font-bold ${k === v ? "bg-slate-900 text-white" : "bg-white"}`}>
              {u.match(/son_([^.]+)\.mp4/)?.[1]?.replace("e_aigu", "é").replace("e_grave_chapeau", "è ê").replace("c-k", "c k") || k + 1}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-3">
        <Btn onClick={() => { ref.current.currentTime = 0; ref.current.play(); }} testid="lesson-replay" color="bg-white"><RotateCcw size={18} /> {fr ? "Revoir" : "Replay"}</Btn>
        {v < videos.length - 1
          ? <Btn onClick={() => setV(v + 1)} testid="lesson-next-video" color="bg-blue-600 text-white">{fr ? "Vidéo suivante" : "Next video"}</Btn>
          : <Btn onClick={onReady} testid="lesson-ready" color="bg-green-500 text-white"><Check size={18} /> {fr ? "Je suis prêt !" : "I'm ready!"}</Btn>}
      </div>
    </div>
  );
}

export default function ReadingLesson() {
  const { childId, lessonId } = useParams();
  const [sp] = useSearchParams();
  const review = sp.get("review") === "1";
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [step, setStep] = useState(review ? "mastery" : "watch");
  const [mq, setMq] = useState(null);
  const [res, setRes] = useState(null);
  const [kiko, setKiko] = useState(null);
  const [style, setStyle] = useState("petit");
  const [session, setSession] = useState("");
  useEffect(() => { if (step === "listen") setSession(`${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`); }, [step]);
  const lang = lessonId.startsWith("FR") ? "fr" : "en";
  const fr = lang === "fr";
  const petit = style === "petit";
  useEffect(() => {
    setD(null); setRes(null); setMq(null); setStep(review ? "mastery" : "watch");
    api.get(`/reading/${childId}/lesson/${lessonId}`).then(({ data }) => setD(data)).catch(() => navigate(`/kid/${childId}/reading`));
    api.get(`/reading/${childId}/home`).then(({ data }) => setStyle(data.style)).catch(() => {});
    return () => stopReading();
  }, [childId, lessonId, review, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step === "mastery" && !mq) api.get(`/reading/${childId}/lesson/${lessonId}/mastery`, { params: { review } }).then(({ data }) => setMq(data.questions));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <ReadingBg petit><div className="p-10 text-center font-bold">…</div></ReadingBg>;
  if (d.lesson.l3 && !review) return (
    <ReadingBg petit={petit}>
      <header className="max-w-4xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate(`/kid/${childId}/reading/${lang}`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="lesson-back"><ArrowLeft /></button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{d.demo ? (fr ? "Leçon d'essai gratuite" : "Free trial lesson") : lessonId} · {fr ? "Niveau 3 avancé" : "Level 3 advanced"}</p>
          <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="lesson-title">{d.lesson.title}</h1>
        </div>
      </header>
      <Level3Lesson d={d} childId={childId} lessonId={lessonId} lang={lang} petit={petit} />
    </ReadingBg>
  );
  const listen = d.exercises.filter((e) => e.step === "Listen");
  const practise = d.exercises.filter((e) => e.step !== "Listen");
  const videos = d.videos.length ? d.videos : [d.flash_video];
  const celebrate = (msg) => { playBigCheers(); setKiko({ n: Date.now(), msg, kind: "win", streak: 10 }); playSay(msg, lang); };

  const afterPractice = async () => {
    const { data } = await api.post(`/reading/${childId}/lesson/${lessonId}/practice`, { session });
    if (data.demo) { setStep("demo_end"); celebrate(fr ? "Bravo ! Tu as fini la leçon d'essai !" : "Well done! You finished the free lesson!"); return; }
    setStep(d.cards.length ? "read" : "mastery");
  };
  const afterMastery = async (r) => {
    let data;
    try {
      ({ data } = await api.post(`/reading/${childId}/lesson/${lessonId}/mastery`, { answers: r.answers, review }));
    } catch {
      toast.error(fr ? "Oups, réessaie dans un instant." : "Oops, please try again in a moment.");
      setMq(null);
      api.get(`/reading/${childId}/lesson/${lessonId}/mastery`, { params: { review } }).then(({ data: q }) => setMq(q.questions)).catch(() => {});
      return;
    }
    setRes(data);
    setStep("result");
    if (data.certificate) celebrate(fr ? "Bravo ! Tu as fini tout le niveau ! Voici ton certificat !" : "Amazing! You finished the whole level! Here is your certificate!");
    else if (data.passed) celebrate(fr ? "Champion ! Leçon réussie !" : "Champion! Lesson mastered!");
    else playSay(fr ? "Bien essayé ! On s'entraîne encore un peu." : "Good try! Let's practise a bit more.", lang);
  };

  return (
    <ReadingBg petit={petit}>
      <header className="max-w-5xl mx-auto px-5 pt-5 flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(d.demo ? `/kid/${childId}/reading` : `/kid/${childId}/reading/${lang}`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="lesson-back"><ArrowLeft /></button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{d.demo ? (fr ? "Leçon d'essai gratuite" : "Free trial lesson") : lessonId}</p>
          <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="lesson-title">{d.lesson.title}</h1>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-5 pt-4">{!review && !["result", "demo_end"].includes(step) && <Steps step={step} fr={fr} />}</div>
      <main className="max-w-5xl mx-auto px-5 py-6 flex flex-col items-center">
        {step === "watch" && <Watch videos={videos} fr={fr} onReady={() => setStep("listen")} />}
        {step === "listen" && <ExerciseRunner key="listen" session={session} childId={childId} lang={lang} petit={petit} items={listen} onDone={() => (d.demo || !practise.length ? afterPractice() : setStep("practise"))} />}
        {step === "practise" && <ExerciseRunner key="practise" session={session} childId={childId} lang={lang} petit={petit} items={practise} onDone={afterPractice} />}
        {step === "read" && <FlashCards cards={d.cards} lang={lang} petit={petit} onDone={() => setStep("mastery")} />}
        {step === "mastery" && mq && (
          <div className="w-full flex flex-col items-center gap-4">
            <p className="rounded-full bg-slate-900 text-white px-5 py-2 font-bold" data-testid="mastery-banner">{review ? (fr ? "Petite révision : 3 questions" : "Quick review: 3 questions") : (fr ? "Le grand défi : 5 questions" : "Mastery check: 5 questions")}</p>
            <ExerciseRunner key="mastery" childId={childId} lang={lang} petit={petit} items={mq} mode="test" onDone={afterMastery} />
          </div>
        )}
        {step === "result" && res && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center gap-4 text-center" data-testid="lesson-result">
            <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-36 h-36" animate={res.passed ? { y: [0, -20, 0], rotate: [0, 10, -10, 0] } : {}} transition={{ repeat: Infinity, duration: 1.2 }} />
            {res.certificate && <p className="rounded-full bg-amber-300 border-[3px] border-slate-900 px-5 py-2 font-bold" data-testid="lesson-level-done">{fr ? `Niveau ${res.certificate.level} terminé !` : `Level ${res.certificate.level} complete!`}</p>}
            <h2 className="text-3xl sm:text-4xl font-bold" data-testid="lesson-result-title">{res.passed ? (fr ? "Leçon maîtrisée !" : "Lesson mastered!") : (fr ? "Presque !" : "Almost there!")}</h2>
            <p className="text-2xl font-bold" data-testid="lesson-result-score">{res.score}/{res.total}</p>
            <div className="flex gap-1">{Array.from({ length: res.stars }).map((_, k) => <motion.span key={k} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + k * 0.2 }}><Star size={40} className="fill-yellow-400 text-yellow-500" /></motion.span>)}</div>
            {res.need_practice && <p className="font-semibold max-w-md">{fr ? `Il faut aussi 8/10 aux exercices (tu as ${res.practice_score}/10). Refais l'entraînement !` : `You also need 8/10 in practice (you have ${res.practice_score}/10). Practise again!`}</p>}
            {res.rewatch && <p className="font-semibold max-w-md text-orange-700" data-testid="lesson-rewatch">{fr ? "Kiko te conseille de revoir la vidéo, puis de réessayer." : "Kiko says: watch the video again, then try again."}</p>}
            <div className="flex flex-wrap gap-3 justify-center">
              {res.certificate && <Btn onClick={() => navigate(`/reading-certificate/${childId}/${res.certificate.lang}/${res.certificate.level}`)} testid="lesson-certificate" color="bg-amber-400"><Award size={18} /> {fr ? "Mon certificat" : "My certificate"}</Btn>}
              {res.next_lesson && <Btn onClick={() => navigate(`/kid/${childId}/reading/lesson/${res.next_lesson}`, { replace: true })} testid="lesson-next-lesson" color="bg-green-500 text-white">{fr ? "Leçon suivante" : "Next lesson"}</Btn>}
              {!res.passed && <Btn onClick={() => { if (review) { navigate(`/kid/${childId}/reading/lesson/${lessonId}`, { replace: true }); return; } setRes(null); setMq(null); setStep(res.rewatch ? "watch" : res.need_practice ? "listen" : "mastery"); }} testid="lesson-retry" color="bg-blue-600 text-white"><RotateCcw size={18} /> {fr ? "Réessayer" : "Try again"}</Btn>}
              <Btn onClick={() => navigate(`/kid/${childId}/reading/${lang}`)} testid="lesson-to-path" color="bg-white">{fr ? "Mon parcours" : "My path"}</Btn>
            </div>
          </motion.div>
        )}
        {step === "demo_end" && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center gap-4 text-center max-w-lg" data-testid="lesson-demo-end">
            <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-40 h-40" animate={{ y: [0, -16, 0] }} transition={{ repeat: Infinity, duration: 1.2 }} />
            <h2 className="text-3xl font-bold">{fr ? "Tu as adoré ? Kiko aussi !" : "Did you love it? Kiko did too!"}</h2>
            <p className="font-semibold text-lg">{fr ? DEMO_MORE.fr : DEMO_MORE.en}</p>
            {!isReadingApp() && <Btn onClick={() => navigate("/pricing?reading=1")} testid="lesson-demo-unlock" color="bg-yellow-300">{fr ? "Débloquer la Lecture" : "Unlock Reading"}</Btn>}
          </motion.div>
        )}
      </main>
      <KikoCheer cheer={kiko} />
    </ReadingBg>
  );
}
